import { memo, useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { buildAccommodationCancellationPolicies, formatDate } from '@luxuryescapes/lib-refunds'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShieldSlashIcon from 'LuxKit/Icons/line/LineShieldSlashIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import { getRefundDescriptions, Refundable } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import Clickable from 'components/Common/Clickable'
import HelpPane from 'components/Common/HelpPane'
import ModalContext from 'contexts/ModalContext'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

function getRefundableStatus(headlineCancellationPolicyType: 'fullyRefundable' | 'partiallyRefundable' | 'nonRefundable'): Refundable {
  switch (headlineCancellationPolicyType) {
    case 'fullyRefundable':
      return Refundable.Fully
    case 'partiallyRefundable':
      return Refundable.Partial
    case 'nonRefundable':
      return Refundable.Non
    default:
      assertUnreachable(headlineCancellationPolicyType)
  }
}

interface Props {
  propertyTimeZone: string
  headlineCancellationPolicyType: 'fullyRefundable' | 'partiallyRefundable' | 'nonRefundable'
  headlineCancellationPolicyStart: Date | undefined
  fullyRefundableUntil: string | undefined
  cancellationPolicies: Array<App.Property.CancellationPolicy>
  currency: string
  shouldClarifyRefundIsHotelOnly: boolean
}

function PropertyCancellationPolicy({
  propertyTimeZone,
  headlineCancellationPolicyType,
  headlineCancellationPolicyStart,
  fullyRefundableUntil,
  cancellationPolicies,
  currency,
  shouldClarifyRefundIsHotelOnly,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const refundDescriptions = useMemo(() => getRefundDescriptions(undefined, intl), [intl])

  const refundableStatus = getRefundableStatus(headlineCancellationPolicyType)
  const isNonRefundable = refundableStatus === Refundable.Non
  const refundDescription = refundDescriptions[refundableStatus]
  const { description: policyDescriptions } = useMemo(() => buildAccommodationCancellationPolicies({
    fullRefundableUntil: fullyRefundableUntil,
    cancellationPolicies: cancellationPolicies.map(({
      startIsoDateTime,
      endIsoDateTime,
      penaltyPrice,
      isFullyNonRefundable,
      originalDescription,
    }) => ({
      // TODO(HP-1333): Update `lib-refunds` to conform to Customer Portal interface.
      amount: penaltyPrice,
      percent: 0,
      description: originalDescription,
      start: startIsoDateTime,
      end: endIsoDateTime,
      nonRefundable: isFullyNonRefundable,
    })),
  }, {
    timezone: propertyTimeZone,
    currencyCode: currency,
    hideExcludesFlights: !shouldClarifyRefundIsHotelOnly,
  }), [
    fullyRefundableUntil,
    cancellationPolicies,
    propertyTimeZone,
    currency,
    shouldClarifyRefundIsHotelOnly,
  ])

  const openModal = useCallback(() => {
    showModal(<Modal
      title={refundDescription}
    >
      {policyDescriptions.map((line, i) => <BodyText variant="large" as="p" key={i}>{ line }</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [
    showModal,
    refundDescription,
    policyDescriptions,
  ])

  const shieldIcon = isNonRefundable
    ? <LineShieldSlashIcon />
    : <SolidShieldCheckIcon colour="highlight-primary-dark" />

  const cancellationLabel = useMemo<React.ReactNode>(() => {
    if (isNonRefundable) return refundDescription
    const styledRefund = <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span" wrap="no-wrap" key="refund">{refundDescription}</BodyText>
    const formattedDate = headlineCancellationPolicyStart
      ? formatDate.formattedShortDateByTimezone(headlineCancellationPolicyStart, propertyTimeZone)
      : undefined
    if (formattedDate && shouldClarifyRefundIsHotelOnly) {
      return intl.formatMessage(
        {
          id: 'nsXp9-TaxkcfoC_Mgmn8',
          defaultMessage: '{refundType} until {date} (hotel only)',
          description: 'Cancellation policy with date and hotel-only clarification',
        },
        { refundType: styledRefund, date: formattedDate },
      )
    }
    if (formattedDate) {
      return intl.formatMessage(
        {
          id: 'nGO52oYKVABrD8dlQrKg',
          defaultMessage: '{refundType} until {date}',
          description: 'Cancellation policy with deadline date in the date selection or calendar picker',
        },
        { refundType: styledRefund, date: formattedDate },
      )
    }
    return styledRefund
  }, [isNonRefundable, headlineCancellationPolicyStart, propertyTimeZone, shouldClarifyRefundIsHotelOnly, intl, refundDescription])

  return <Clickable onClick={openModal}>
    <BodyTextBlock variant="medium" startIcon={shieldIcon}>
      <FormattedMessage
        id="aBR0Mwy9eVd8FXMnzQYq"
        defaultMessage="{cancellationLabel} <icon></icon>"
        description="Cancellation policy label with info icon in the cancellation flow"
        values={{
          cancellationLabel,
          icon: () => <LineInfoCircleIcon size="XS" />,
        }}
      />
    </BodyTextBlock>
  </Clickable>
}

export default memo(PropertyCancellationPolicy)
